/** Requisition, the armory, readiness and the company's numbers (04 §2, §4.4, §6; 02 §8). */

import { RANGER, STANDARD_ISSUE as SIM_STANDARD_ISSUE, WEIGHT as SIM_WEIGHT } from '../../sim/tuning';
import type { Inventory } from '../../sim/types';
import type { ArmoryItem, SpecialItem } from '../types';

/**
 * Free on every mission: the original's default loadout, 22 of 30 carry units (02 §8). The sim
 * owns these numbers (src/sim/tuning.ts); the loadout screen must agree with it, so they are read, not copied.
 */
export const STANDARD_ISSUE: Inventory = { ...SIM_STANDARD_ISSUE };

/** The C64's fixed pod, offered as a toggle in Classic (02 §8). */
export const FIXED_POD: Inventory = { magazines: 2, roundsInMag: 0, grenades: 2, laws: 1, bombs: 1, firstAid: 1 };

/**
 * RP per unit (04 §6). `roundsInMag` is how full the loaded magazine is, not a thing to buy. Rare
 * equipment is found, not bought: it was paid for when the cache's 15 RP was turned down, and it
 * comes home with the ranger - so taking it from stock is free.
 */
export const PRICE: Record<ArmoryItem, number> = {
  magazines: 1, roundsInMag: 0, grenades: 2, laws: 6, bombs: 4, firstAid: 3,
  smoke: 0, nvg: 0, 'wire-cutters': 0, 'suppressed-pistol': 0, ghillie: 0,
};

/** Carry units per item (02 §7-§8); smoke is per grenade. */
export const WEIGHT: Record<ArmoryItem, number> = {
  magazines: SIM_WEIGHT.magazine, roundsInMag: 0, grenades: SIM_WEIGHT.grenade, laws: SIM_WEIGHT.law,
  bombs: SIM_WEIGHT.bomb, firstAid: SIM_WEIGHT.firstAid, smoke: 1, nvg: 1, 'wire-cutters': 1, 'suppressed-pistol': 2,
  ghillie: 2,
};

/** The items a pod or the ranger's `Inventory` can hold; rare equipment rides in `Loadout.gear` instead. */
export const PACKABLE: ReadonlyArray<keyof Inventory> = ['magazines', 'grenades', 'laws', 'bombs', 'firstAid'];

export const RARE_ITEMS: readonly SpecialItem[] = ['smoke', 'nvg', 'wire-cutters', 'suppressed-pistol', 'ghillie'];

/** Rare equipment on the ranger (04 §6): a pistol is one item and always comes with this many rounds. */
export const GEAR = { pistolRounds: 14, maxSmoke: 3 } as const;

/** What a Supply Cache's equipment choice adds to the stock, and what a Quartermaster unlock starts a run with. */
export const CACHE_FIND: Record<SpecialItem, number> = {
  smoke: 2, nvg: 1, 'wire-cutters': 1, 'suppressed-pistol': 1, ghillie: 1,
};

export const CAPS = { carry: RANGER.carryCap, pod: 21, pods: 3, roundsInMag: SIM_STANDARD_ISSUE.roundsInMag } as const;

export const COMPANY = {
  recruits: 4,
  cap: 6,
  veteranSlots: 1,
  startRp: 30,
  /** RP for a replacement recruit from HQ; the run is lost when nobody can deploy and this cannot be paid. */
  recruitCost: 15,
  medevacCost: 10,
  /** Hall of Honor size, and inductions per won operation (two from Hard upward). */
  hallCap: 6,
  inductions: [1, 1, 2, 2] as readonly number[],
  classicSlots: 6,
  maxNameLength: 16,
} as const;

export const REWARD = {
  /** Node reward = base + perThreat x archetype base threat + perTheatre x theatre index (+ keystone). */
  base: 8,
  perThreat: 2,
  perTheatre: 2,
  keystoneBonus: 10,
  /** Success pays reward x multiplier x clamp(successFloor + score / scorePerUnit, .., successCap). */
  successFloor: 0.8,
  successCap: 1.5,
  scorePerUnit: 10000,
  /** A failed mission still pays for the damage done, up to this share. */
  failCap: 0.3,
  failScorePerUnit: 8000,
  supplyRp: 15,
} as const;

export const READINESS = {
  min: 0,
  max: 10,
  attempted: 1,
  quietExtraction: -1,
  alarmExtraction: 1,
  failed: 1,
  captured: 1,
  keystone: -2,
  scrubbed: 1,
  /** Alert levels that count as quiet / as the full alarm (04 §2.3). */
  quietAlert: 1,
  alarmAlert: 3,
} as const;

/** Missions to sit out by wound state on extraction (04 §4.4). */
export const RECOVERY = { serious: 1, critical: 2, mia: 2, seriousWounds: 2 } as const;

export const FATIGUE = {
  /** Stamina pool lost per fatigue point above `freePoints`. */
  perPoint: 0.15,
  freePoints: 1,
  floor: 0.4,
} as const;

/** Meta-currency awards (04 §5). */
export const COMMENDATIONS = {
  medal: 1,
  medalOfHonor: 3,
  keystone: 1,
  firstCompletion: 1,
  operationWon: 5,
  perDifficulty: 2,
} as const;

export const DIFFICULTY_NAMES: readonly string[] = ['Milk Run', 'Standard', 'Hard', 'Suicide'];

/** Same keystone failed this many times: the operation is lost (04 §2.4). */
export const KEYSTONE_FAILURES_ALLOWED = 2;
